import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './HomePage.css';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import ScrollStory from '../../components/ScrollStory/ScrollStory';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import ConfirmationPopup from '../../components/ConfirmationPopup/ConfirmationPopup';
import { isAndroidCapacitor } from '../../utils/platformUtils';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { apiFetch, apiFetchCached } from '../../utils/apiClient';
import {
    HOME_PAGE_SECTION_HASHES,
    isHomePageSectionHash,
    scrollToHomePageHash,
} from '../../utils/homePageSectionScroll';
import PageLoader from '../../components/PageLoader/PageLoader';
import WishlistHeartButton from '../../components/WishlistHeartButton/WishlistHeartButton';

import { useNotifications } from '../../components/NotificationSystem/NotificationSystem';

// Add a simple sparkle/confetti effect component
const Sparkle = () => (
    <span className="sparkle" role="img" aria-label="sparkle">✨</span>
);

const AnimatedHeroCardCounter = ({ value }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const [showSparkle, setShowSparkle] = useState(false);
    const animationRef = useRef(null);
    const prevValue = useRef(value);

    useEffect(() => {
        if (value == null) return;
        cancelAnimationFrame(animationRef.current);
        let startValue = displayValue;
        const startTime = performance.now();
        const duration = 1200;
        const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const nextValue = Math.floor(startValue + (value - startValue) * progress);
            setDisplayValue(nextValue);
            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };
        animationRef.current = requestAnimationFrame(animate);
        // Show sparkle/confetti if value increases
        if (value > prevValue.current) {
            setShowSparkle(true);
            setTimeout(() => setShowSparkle(false), 1200);
        }
        prevValue.current = value;
        return () => cancelAnimationFrame(animationRef.current);
    }, [value]);

    return (
        <div className="hero-card-counter-visual">
            <span className="hero-card-counter-number">{displayValue.toLocaleString()}</span>
            {showSparkle && <Sparkle />}
        </div>
    );
};

const MEMBERSHIP_PLANS = [
    {
        id: 'free',
        name: 'Fan',
        price: 'Free',
        priceNote: 'forever',
        badge: null,
        benefits: [
            'Browse collectibles, hero cards & marketplace',
            'Add items to your collection, wish list & marketplace',
            'Manage your profile & catalog your memorabilia',
            'Track what you paid and collection value',
            'Access collector blogs & community content',
        ],
        cta: 'Join as a Fan',
        featured: false,
    },
    {
        id: 'pro',
        name: 'Superfan',
        price: '$2.99',
        priceNote: '/ month',
        badge: 'Popular',
        benefits: [
            'Everything in Fan',
            'Ad-free experience across the site',
            'Participate in fantasy games',
            'Use Collector Coins to bid on monthly auction prizes',
            'Superfan badge on your profile',
        ],
        cta: 'Become a Superfan',
        featured: true,
        comingSoon: true,
    },
    {
        id: 'elite',
        name: 'VIP',
        price: '$4.99',
        priceNote: '/ month',
        badge: null,
        benefits: [
            'Everything in Superfan',
            'Marketplace listings 1 day before Fan and Superfan members',
            'VIP badge on profile & marketplace listings',
        ],
        cta: 'Become a VIP',
        featured: false,
        comingSoon: true,
    },
];

const ABOUT_BENEFITS = [
    {
        id: 'vault',
        stall: '01',
        title: 'Explore collections',
        tag: 'Browse what collectors own across race-used gear, diecasts, cards & more',
        icon: (
            <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <rect x="8" y="14" width="32" height="26" rx="4" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <path d="M16 14v-2a8 8 0 0 1 16 0v2" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="24" cy="27" r="3.5" fill="currentColor" />
                <path d="M24 30.5v5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        id: 'worth',
        stall: '02',
        title: 'Track your value',
        tag: 'See what your collection is worth over time',
        icon: (
            <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <path d="M8 36l10-12 8 6 14-18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 12h10v10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        id: 'marketplace',
        stall: '03',
        title: 'Buy & sell',
        tag: 'List items, browse deals, and message sellers',
        icon: (
            <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <path d="M8 30h32l-3 8H11l-3-8z" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
                <path d="M12 30V18l6-6h12l6 6v12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
                <circle cx="16" cy="38" r="3" fill="currentColor" />
                <circle cx="32" cy="38" r="3" fill="currentColor" />
            </svg>
        ),
    },
    {
        id: 'heroes',
        stall: '04',
        title: 'Hero Card Archive',
        tag: 'Search and add hero cards with other collectors',
        icon: (
            <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <rect x="12" y="8" width="24" height="32" rx="3" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="24" cy="20" r="5" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <path d="M16 34c2.5-4 13.5-4 16 0" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        id: 'wishlist',
        stall: '05',
        title: 'Build a wish list',
        tag: 'Save items you want so you don’t forget them',
        icon: (
            <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <path
                    d="M24 40s-14-8.5-14-18a8 8 0 0 1 14-5.3A8 8 0 0 1 38 22c0 9.5-14 18-14 18z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                />
            </svg>
        ),
    },
    {
        id: 'rewards',
        stall: '06',
        title: 'Earn rewards',
        tag: 'Get coins for activity and bid in monthly auctions',
        icon: (
            <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <path d="M16 18h16v6a8 8 0 0 1-16 0v-6z" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <path d="M16 18V12h16v6" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <path d="M12 14h4M32 14h4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M20 32h8v8h-8z" fill="none" stroke="currentColor" strokeWidth="2.5" />
                <path d="M18 40h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
];

const AnimatedNumber = ({ value }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const animationRef = useRef(null);

    useEffect(() => {
        if (value == null) return;

        cancelAnimationFrame(animationRef.current);

        let startValue = displayValue;
        const startTime = performance.now();
        const duration = 1000;

        const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const nextValue = Math.floor(startValue + (value - startValue) * progress);
            setDisplayValue(nextValue);

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationRef.current);
    }, [value]);

    return <div className="stat-value">{displayValue.toLocaleString()}</div>;
};

const MembershipPlanCard = ({ plan, onSelect }) => (
    <article
        data-plan-id={plan.id}
        className={`HomePage-membership-card ${plan.featured ? 'is-featured' : ''} ${plan.comingSoon ? 'is-coming-soon' : ''}`}
    >
        {plan.comingSoon && (
            <span className="HomePage-membership-badge msv-badge msv-badge-dark">
                Coming Soon
            </span>
        )}
        {plan.badge && (
            <span className={`msv-badge msv-badge-gold ${plan.comingSoon ? 'HomePage-membership-secondary-badge' : 'HomePage-membership-badge'}`}>
                {plan.badge}
            </span>
        )}
        <header className="HomePage-membership-card-header">
            <h3 className="HomePage-membership-plan-name">{plan.name}</h3>
            <div className="HomePage-membership-price">
                <span className="HomePage-membership-price-amount">{plan.price}</span>
                {plan.priceNote && (
                    <span className="HomePage-membership-price-note">{plan.priceNote}</span>
                )}
            </div>
        </header>
        <ul className="HomePage-membership-benefits">
            {plan.benefits.map((benefit) => (
                <li key={benefit} className="HomePage-membership-benefit">
                    <span className="HomePage-membership-benefit-mark" aria-hidden="true" />
                    {benefit}
                </li>
            ))}
        </ul>
        <button
            type="button"
            className={`cta-main-btn HomePage-membership-cta ${plan.featured ? 'HomePage-membership-cta--featured' : ''} ${plan.comingSoon ? 'HomePage-membership-cta--coming-soon' : ''}`}
            onClick={() => {
                if (!plan.comingSoon) onSelect(plan.id);
            }}
            disabled={plan.comingSoon}
        >
            {plan.comingSoon ? 'Coming Soon' : plan.cta}
        </button>
    </article>
);

const HomePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { success, error } = useNotifications();
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [contact, setContact] = useState({
        name: '',
        email: '',
        message: ''
    });

    const [recentCollectibles, setRecentCollectibles] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [sitewideStats, setSitewideStats] = useState(null);
    const [lanePaused, setLanePaused] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const swipeStartX = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [sendMsgLoading, setSendMsgLoading] = useState(false);
    const wantsSectionScroll = isHomePageSectionHash(location.hash);
    const [hashScrollReady, setHashScrollReady] = useState(!wantsSectionScroll);

    useEffect(() => {
        const hitKey = 'msv_homePageHit';
        if (sessionStorage.getItem(hitKey)) return;
        sessionStorage.setItem(hitKey, '1');
        apiFetch('/Users/HomePageHit', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
            // no body
        }).catch((err) => {
            sessionStorage.removeItem(hitKey);
            console.error('Failed to record home page hit', err);
        });
    }, []);

    useEffect(() => {
        const fetchHomePageData = async () => {
            try {
                const response = await apiFetchCached('/Collectibles/GetHomePageData', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                }, { ttlMs: 60000 });

                if (response.ok) {
                    const data = await response.json();

                    setRecentCollectibles(data.searchCollectiblesResultDto?.collectibleSearchResultDtos || []);
                    setSitewideStats(data.sitewideDataResultDto || null);
                } else {
                    console.error('Failed to fetch home page data');
                }
            } catch (err) {
                console.error('Failed to fetch home page data', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHomePageData();
    }, []);

    // Jump straight to in-page sections on hash (e.g. Sign Up → Join Our Community)
    useLayoutEffect(() => {
        if (!isHomePageSectionHash(location.hash)) {
            setHashScrollReady(true);
            return undefined;
        }

        setHashScrollReady(false);

        const runScroll = () => {
            const didScroll = scrollToHomePageHash(location.hash);
            if (didScroll) {
                setHashScrollReady(true);
            }
            return didScroll;
        };

        if (runScroll()) {
            return undefined;
        }

        let rafId2;
        const rafId1 = requestAnimationFrame(() => {
            if (runScroll()) return;
            rafId2 = requestAnimationFrame(runScroll);
        });

        return () => {
            cancelAnimationFrame(rafId1);
            if (rafId2) cancelAnimationFrame(rafId2);
        };
    }, [location.hash, isLoading]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setContact((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setSendMsgLoading(true);
        try {
            const response = await apiFetch('/ContactUs/SubmitContactForm', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(contact),
            });
            const result = await response.json();
            if (response.ok) {
                success('Message sent successfully! We\'ll get back to you soon.');
                setContact({ name: '', email: '', message: '' });
            } else {
                error('Failed to send message. Please try again.');
                console.error('Failed to submit form:', result);
            }
        } catch (err) {
            error('An error occurred. Please try again later.');
            console.error('Error occurred during submission:', err);
        } finally {
            setSendMsgLoading(false);
        }
    };

    const showPreviousCollectible = () => {
        setCurrentIndex((prev) =>
            prev === 0 ? recentCollectibles.length - 1 : prev - 1
        );
    };

    const showNextCollectible = () => {
        setCurrentIndex((prev) =>
            (prev + 1) % recentCollectibles.length
        );
    };

    const showCollectibleAtIndex = (index) => {
        if (index === currentIndex) return;
        setCurrentIndex(index);
    };

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => setPrefersReducedMotion(mq.matches);
        apply();
        if (mq.addEventListener) {
            mq.addEventListener('change', apply);
            return () => mq.removeEventListener('change', apply);
        }
        mq.addListener(apply);
        return () => mq.removeListener(apply);
    }, []);

    useEffect(() => {
        if (prefersReducedMotion || lanePaused || recentCollectibles.length < 2) {
            return undefined;
        }
        const id = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % recentCollectibles.length);
        }, 5000);
        return () => clearInterval(id);
    }, [prefersReducedMotion, lanePaused, recentCollectibles.length]);

    const handleLaneTouchStart = (e) => {
        swipeStartX.current = e.changedTouches[0].clientX;
        setLanePaused(true);
    };

    const handleLaneTouchEnd = (e) => {
        if (swipeStartX.current == null) return;
        const delta = e.changedTouches[0].clientX - swipeStartX.current;
        swipeStartX.current = null;
        if (Math.abs(delta) > 48) {
            if (delta < 0) showNextCollectible();
            else showPreviousCollectible();
        }
        setLanePaused(false);
    };

    const handleConfirmPopupClose = () => {
        setShowConfirmPopup(false);
    };

    if (isLoading && !wantsSectionScroll) {
        return <PageLoader />;
    }

    return (
        <div className={`HomePage ${isAndroidCapacitor() ? 'is-android-capacitor' : ''}${wantsSectionScroll && !hashScrollReady ? ' is-pending-hash-scroll' : ''}`}>
            {!HOME_PAGE_SECTION_HASHES.has(location.hash) && <ScrollToTop />}
            <ConfirmationPopup
                show={showConfirmPopup}
                message="Your message has been sent successfully!"
                onConfirm={handleConfirmPopupClose}
            />

            <div className="HomePage-Logorow">
              
                <div className="HomePage-logo-overlay">
                    <img
                        src={require('./msv_header_logo.png')}
                        alt="My Speed Vault Logo"
                        className="fade-zoom-in"
                    />
                </div>
                <div className="HomePage-logo-overlay">
                <div className="reveal-animation">
                    <img
                    src={require('./msv_home_page_logo.png')}
                    alt="My Speed Vault Logo"
                    />
                </div>
                </div>


            </div>

            {!isLoading && recentCollectibles.length > 0 && (
                <section className="HomePage-recent-items-section">
                    
                    <div className="HomePage-section-content visible victory-lane">
                        <header className="victory-lane-header">
                          
                            <h1 className="HomePage-section-title victory-lane-title">Recently Added</h1>
                            <div className="msv-finish-line" aria-hidden="true" />
                        </header>

                        <div
                            className={`victory-lane-stage ${prefersReducedMotion ? 'is-reduced-motion' : ''}`}
                            onMouseEnter={() => setLanePaused(true)}
                            onMouseLeave={() => setLanePaused(false)}
                            onFocusCapture={() => setLanePaused(true)}
                            onBlurCapture={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                    setLanePaused(false);
                                }
                            }}
                            onTouchStart={handleLaneTouchStart}
                            onTouchEnd={handleLaneTouchEnd}
                        >
                            <div className="victory-lane-spotlight" aria-hidden="true" />
                            <div className="victory-lane-floor" aria-hidden="true" />

                            <button
                                type="button"
                                className="carousel-button left victory-lane-nav"
                                onClick={showPreviousCollectible}
                                aria-label="Previous collectible"
                            >
                                {"\u276E"}
                            </button>

                            <div className="carousel-outer-wrapper victory-lane-track">
                                <div className="carousel-cards-wrapper victory-lane-cards" style={{ perspective: '1200px' }}>
                                    {recentCollectibles.map((item, index) => {
                                        const len = recentCollectibles.length;
                                        let offset = index - currentIndex;
                                        if (offset > Math.floor(len / 2)) offset -= len;
                                        if (offset < -Math.ceil(len / 2)) offset += len;
                                        if (Math.abs(offset) > 2) return null;

                                        let cardClass = 'far-side';
                                        if (offset === 0) cardClass = 'center';
                                        else if (Math.abs(offset) === 1) cardClass = 'mid-side';
                                        if (offset < 0) cardClass += ' is-left';
                                        else if (offset > 0) cardClass += ' is-right';

                                        return (
                                            <div
                                                key={item.collectionItemId}
                                                className={`carousel-card-5 victory-lane-card ${cardClass}`}
                                                onClick={() => { window.location.href = `/collectionItem/${item.collectionItemId}`; }}
                                                role="button"
                                                tabIndex={offset === 0 ? 0 : -1}
                                                onKeyDown={(e) => {
                                                    if (offset === 0 && (e.key === 'Enter' || e.key === ' ')) {
                                                        e.preventDefault();
                                                        window.location.href = `/collectionItem/${item.collectionItemId}`;
                                                    }
                                                }}
                                                aria-hidden={offset !== 0}
                                            >
                                                <span className="msv-badge msv-badge-gold victory-lane-badge">Just Added</span>
                                                <div className="victory-lane-media">
                                                    <img
                                                        src={resolveImageSrc(item) || 'https://via.placeholder.com/250'}
                                                        alt={item.title}
                                                        className="carousel-image"
                                                    />
                                                    <WishlistHeartButton itemType="collectible" item={item} />
                                                </div>
                                                <div className="carousel-card-title-container victory-lane-title-plate">
                                                    <h3>{toTitleCase(item.title)}</h3>
                                                </div>

                                                <div className="carousel-card-overlay">
                                                    <div className="carousel-overlay-content">
                                                        <button type="button" className="carousel-view-details-btn msv-shine">
                                                            View Details
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="victory-lane-reflection" aria-hidden="true" />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <button
                                type="button"
                                className="carousel-button right victory-lane-nav"
                                onClick={showNextCollectible}
                                aria-label="Next collectible"
                            >
                                {"\u276F"}
                            </button>
                        </div>

                        <div className="carousel-dots victory-lane-lights" role="tablist" aria-label="Recently added slides">
                            {recentCollectibles.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    role="tab"
                                    aria-selected={index === currentIndex}
                                    className={`carousel-dot victory-lane-light ${index === currentIndex ? 'active' : ''} ${index < currentIndex ? 'is-passed' : ''}`}
                                    onClick={() => showCollectibleAtIndex(index)}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
               
                     <div className="HomePage-section-content HomePage-about">
                        <h1 className="HomePage-about-us">ABOUT US</h1>
                        <div className='HomePage-about-heading'>
                        <p className="HomePage-about-text">
                        MySpeedVault is the ultimate home for motorsports memorabilia collectors. Fans and collectors unite here to build the world's largest database of race-used gear, diecasts, trading cards, hero cards, and more - then buy, sell, and connect through Marketplace, messages, and wish lists. Whether you've spent years hunting rarities or just scored your first piece, this is where you catalog, showcase, and grow your collection with the community!
                       
                        </p>
                        </div>
                    <ul className="HomePage-about-benefits" aria-label="What you can do on MySpeedVault">
                        {ABOUT_BENEFITS.map((benefit) => (
                            <li
                                key={benefit.id}
                                className={`HomePage-about-benefit HomePage-about-benefit--${benefit.id}`}
                            >
                                <span className="HomePage-about-benefit-stall" aria-hidden="true">
                                    {benefit.stall}
                                </span>
                                <span className="HomePage-about-benefit-icon" aria-hidden="true">
                                    {benefit.icon}
                                </span>
                                <div className="HomePage-about-benefit-copy">
                                    <strong className="HomePage-about-benefit-title">{benefit.title}</strong>
                                    <span className="HomePage-about-benefit-tag">{benefit.tag}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                    </div>

                    <ScrollStory mode="split">
                        <div className="HomePage-marketplace-inner HomePage-marketplace-inner--panel">
                            <p className="HomePage-marketplace-eyebrow">The Grid Market</p>
                            <h1 className="HomePage-marketplace-title">Buy &amp; Sell on Marketplace</h1>
                            <div className="msv-finish-line HomePage-marketplace-finish" aria-hidden="true" />
                            <p className="HomePage-marketplace-lead">
                                Motorsports commerce built for collectors - list memorabilia, browse by price and location,
                                message sellers directly, and earn trust with buyer and seller reviews.
                               
                            </p>
                            <ul className="HomePage-marketplace-features">
                                <li className="HomePage-marketplace-feature">
                                    <span className="HomePage-marketplace-feature-mark" aria-hidden="true" />
                                    <div>
                                        <strong>List for sale</strong>
                                        <span>Price, location, shipping, and photos in one listing.</span>
                                    </div>
                                </li>
                                <li className="HomePage-marketplace-feature">
                                    <span className="HomePage-marketplace-feature-mark" aria-hidden="true" />
                                    <div>
                                        <strong>Message sellers</strong>
                                        <span>Negotiate and arrange pickup from any listing.</span>
                                    </div>
                                </li>
                                <li className="HomePage-marketplace-feature">
                                    <span className="HomePage-marketplace-feature-mark" aria-hidden="true" />
                                    <div>
                                        <strong>Rate the grid</strong>
                                        <span>Buyer &amp; seller reviews that build community trust.</span>
                                    </div>
                                </li>
                            </ul>
                            <button type="button" className="cta-main-btn HomePage-marketplace-cta" onClick={() => navigate('/marketplace')}>
                                Explore Marketplace
                            </button>
                        </div>
                    </ScrollStory>

                    {/* Collectibles Stats Section */}
                    {!isLoading && sitewideStats && (
                        <section className="HomePage-stats-section HomePage-section-content">
                            <div className="HomePage-stats-subsection">
                                <div className="HomePage-stats-header">
                                    <h1 className="HomePage-stats-title">
                                        <span className="HomePage-stats-title-line">collectibles to</span>
                                        <span className="HomePage-stats-title-line HomePage-stats-title-explore">explore</span>
                                    </h1>
                                    <p className="HomePage-stats-bar" aria-hidden="true"></p>

                                    <Link to="/searchCollectibles" className="HomePage-stat-box-link">
                                    <div className="HomePage-stat-box-main">
                                        <AnimatedNumber value={sitewideStats.numberOfCollectibles} />
                                        <div className="stat-label">Total Collectibles</div>
                                    </div>
                                    </Link>
                                </div>
                                   
                                    <div className="HomePage-stat-row">
                                        <Link to="/searchCollectibles?driver=&series=&mainCategory=Race+Used+%2F+Team+Issued&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="HomePage-stat-box-link">
                                        <div className="HomePage-stat-box">
                                            <AnimatedNumber value={sitewideStats.numberOfRaceUsedCollectibles} />
                                            <div className="stat-label">Race Used</div>
                                        </div>
                                        </Link>
                                        <Link to="/searchDiecasts?page=1" className="HomePage-stat-box-link">
                                        <div className="HomePage-stat-box">
                                            <AnimatedNumber value={sitewideStats.numberOfDiecastCollectibles} />
                                            <div className="stat-label">Diecasts</div>
                                        </div>
                                        </Link>
                                        <Link to="/searchCollectibles?driver=&series=&mainCategory=Trading+Cards&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="HomePage-stat-box-link">
                                        <div className="HomePage-stat-box">
                                            <AnimatedNumber value={sitewideStats.numberOfTradingCardCollectibles} />
                                            <div className="stat-label">Trading Cards</div>
                                        </div>
                                        </Link>
                                        <Link to="/searchCollectibles?driver=&series=&mainCategory=Hero+Cards+%2F+Post+Cards&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="HomePage-stat-box-link">
                                        <div className="HomePage-stat-box">
                                            <AnimatedNumber value={sitewideStats.numberOfHeroCardCollectibles} />
                                            <div className="stat-label">Hero Cards</div>
                                        </div>
                                        </Link>
                                        <Link to="/searchCollectibles?driver=&series=&mainCategory=Programs+%2F+Tickets&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="HomePage-stat-box-link">
                                        <div className="HomePage-stat-box">
                                            <AnimatedNumber value={sitewideStats.numberOfProgramTicketCollectibles} />
                                            <div className="stat-label">Programs & Tickets</div>
                                        </div>
                                        </Link>
                               
                                </div>
                            </div>
                        </section>
                    )}

           
            </section>
            )}

            {/* Hero Card Archive Section */}
            {!isLoading && sitewideStats && (
                <section className="HomePage-hero-card-archive-section" aria-labelledby="hero-card-archive-title">
                    <div className="hero-card-archive-inner">
                        <div className="hero-card-archive-main">
                            <h2 id="hero-card-archive-title" className="hero-card-archive-title">
                                The Hero Card Archive
                            </h2>
                            <div className="hero-card-archive-stat">
                                <AnimatedHeroCardCounter value={sitewideStats.numberOfHeroCardsInHeroCardArchive || 0} />
                                <span className="hero-card-archive-and-counting">
                                    <span className="hero-card-archive-label">Hero Cards</span>
                                    <span className="and-counting-animated">and Counting</span>
                                </span>
                            </div>
                            <button
                                type="button"
                                className="cta-main-btn"
                                onClick={() => navigate('/searchHeroCards')}
                            >
                                Browse Them All
                            </button>
                        </div>

                        <div className="hero-card-archive-side">
                            <div className="hero-card-archive-add-cta">
                                <p className="hero-card-archive-add-cta-text">
                                    <strong>Have a Rare Card?</strong> Add it to the Archive and help fellow collectors.
                                </p>
                                <button
                                    type="button"
                                    className="cta-main-btn"
                                    onClick={() => navigate('/additems?type=heroCard')}
                                >
                                    Add Your Hero Card
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Join Our Community Section */}
            <section id="HomePage-community-section" className="HomePage-community-wrapper" aria-labelledby="HomePage-community-title">
                <div className="HomePage-community-content">
                    <h2 id="HomePage-community-title" className="HomePage-community-title">Join Our Community</h2>
                    <p className="HomePage-community-caption">
                        Connect with fellow motorsports enthusiasts and showcase your collection to the world.
                        Pick the plan that fits your pit lane.
                    </p>

                    <div className="HomePage-membership-grid">
                        {MEMBERSHIP_PLANS.filter((plan) => plan.id === 'free').map((plan) => (
                            <MembershipPlanCard key={plan.id} plan={plan} onSelect={(id) => navigate('/signup', { state: { plan: id } })} />
                        ))}
                        <div className="HomePage-membership-paid-stack">
                            {MEMBERSHIP_PLANS.filter((plan) => plan.id !== 'free').map((plan) => (
                                <MembershipPlanCard key={plan.id} plan={plan} onSelect={(id) => navigate('/signup', { state: { plan: id } })} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>
              
            {/* FAQ & Contact Section */}
            <section id="HomePage-contact-faq-section" className="HomePage-contact-faq-section">
              
                <div className="HomePage-contact-overlay">
                
                    <div className="HomePage-contact-container">
                        <div className="HomePage-contact-card">
                            <h1 className="HomePage-section-title">Get in Touch</h1>
                            <p className="HomePage-contact-description">
                                We'd love to hear from you! Reach out to us for inquiries, support, feedback or partnerships.
                            </p>
                            <form className="HomePage-contact-form" onSubmit={handleContactSubmit}>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Your Name"
                                    className="HomePage-contact-input"
                                    required
                                    onChange={handleInputChange}
                                    value={contact.name}
                                />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Your Email"
                                    className="HomePage-contact-input"
                                    required
                                    onChange={handleInputChange}
                                    value={contact.email}
                                />
                                <textarea
                                    name="message"
                                    placeholder="Your Message"
                                    className="HomePage-contact-textarea"
                                    required
                                    onChange={handleInputChange}
                                    value={contact.message}
                                ></textarea>
                                <button type="submit" className={`cta-main-btn ${sendMsgLoading ? 'disabled' : ''}`} disabled={sendMsgLoading}>
    
                                    {sendMsgLoading ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
                  
                <div>
                     
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default HomePage;
